"use client";

import { ArrowRight, Clock, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhoUsesDpmOemPrograms() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const workshops = [{
    date: "15 March 2025",
    title: "Aerospace Component Marking",
    description: "Precision laser marking solutions for critical aerospace components, ensuring full traceability and compliance with rigorous industry standards.",
    location: "Production Facility",
    duration: "4 hours",
    instructor: "Mark Sterling",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineer_mark_sterling.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_laser_marking.png",
    alt: "Precision laser marking on aerospace metal components",
    href: "/DirectPartMarkingDPMPage"
  }, {
    date: "8 April 2025",
    title: "Medical Device Traceability",
    description: "Advanced DPM techniques for medical devices, focusing on permanent, high-contrast identification that withstands sterilization and harsh environments.",
    location: "Clean Room Lab",
    duration: "6 hours",
    instructor: "Sarah Jenkins",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineer_sarah_jenkins.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/medical_device_engraving.png",
    alt: "Micro-engraving on surgical stainless steel instruments",
    href: "/DirectPartMarkingDPMPage"
  }, {
    date: "22 April 2025",
    title: "Automotive OEM Standards",
    description: "Streamlined marking workflows for automotive parts, meeting OEM requirements for durability, readability, and rapid production cycles.",
    location: "Production Facility",
    duration: "3 hours",
    instructor: "Mark Sterling",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineer_mark_sterling.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/automotive_part_marking.png",
    alt: "Automotive engine component with laser-etched serial numbers",
    href: "/DirectPartMarkingDPMPage"
  }, {
    date: "10 May 2025",
    title: "Industrial Asset Tracking",
    description: "Robust identification solutions for heavy industrial assets, utilizing deep engraving to ensure long-term legibility in extreme conditions.",
    location: "Production Facility",
    duration: "2 hours",
    instructor: "Sarah Jenkins",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineer_sarah_jenkins.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_asset_tagging.png",
    alt: "Heavy duty industrial metal plate with deep laser engraving",
    href: "/DirectPartMarkingDPMPage"
  }];
  return <section data-section-id="17" id="who-uses-dpm-oem-programs" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-16 md:mb-20 lg:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.3em] mb-6 text-chart-3">
            Industry Applications
          </p>
          <h2 className="font-default font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-foreground">
            Who uses our{" "}
            <em className="italic text-chart-3">
              DPM programs
            </em>
            .
          </h2>
          <div className="mx-auto mt-8 h-px w-16 bg-chart-3" />
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 lg:gap-12">
          {workshops.map((workshop, i) => <article key={i} data-index={i} className={`group flex flex-col overflow-hidden rounded-lg bg-card border border-border shadow-lg hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
          transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
        }}>
              {}
              <div className="relative overflow-hidden aspect-[4/3] md:aspect-[5/4]">
                <img src={workshop.image} data-wvc-srcset={`${workshop.image}-wvc-srcset`} data-wvc-sizes={`${workshop.image}-wvc-sizes`} alt={workshop.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
              </div>

              {}
              <div className="flex-1 flex flex-col p-8 md:p-10 bg-card">
                

                <h3 className="font-default font-light text-2xl md:text-3xl leading-tight text-card-foreground mb-4">
                  {workshop.title}
                </h3>

                <p className="font-default text-sm md:text-[15px] leading-relaxed text-muted-foreground mb-6 flex-1">
                  {workshop.description}
                </p>

                {}
                

                {}
                
              </div>
            </article>)}
        </div>

        {}
        <div ref={footerRef} className={`mt-16 md:mt-20 flex justify-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="text-foreground hover:no-underline font-default text-base p-0 h-auto">
            <Link to="/CapabilitiesPage" className="inline-flex items-center gap-2 group/all">
              <span className="border-b pb-1 transition-colors duration-300 border-chart-3">
                View All Capabilities
              </span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover/all:translate-x-1 text-chart-3" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}